import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, type Portal } from '@/auth/AuthContext';

/**
 * Route guard that enforces "you must be authenticated AND your role
 * routes you to this portal". Two failure modes:
 *
 *   - not authenticated     → /login (with `from` so we can come back)
 *   - wrong portal for role → redirect to the user's actual portal home
 *                              (a platform admin who navigates to /tenant
 *                              gets sent to /platform, and vice versa)
 *
 * We deliberately don't surface a 403 page for the wrong-portal case —
 * it's not an authorization error, it's a routing nudge.
 */
export function RequirePortal({
  portal,
  children,
}: {
  portal: Portal;
  children: React.ReactNode;
}) {
  const { isAuthenticated, portal: userPortal } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (userPortal !== portal) {
    return <Navigate to={userPortal === 'platform' ? '/platform' : '/tenant'} replace />;
  }
  return <>{children}</>;
}
